import { createSlice, current } from '@reduxjs/toolkit'

import {
  getLeadStatuses,
  getContactInfoTypes,
  getIndustries,
  getCamsHosts,
  getLeadInvalidLostReasons,
  getExternalApiList,
  getExternalApiDetail,
  getExternalApiKeys,
  getCustomLeadFormFields,
} from 'services/settings'

import {
  GetContactInfoTypeResponse,
  GetLeadStatusesResponse,
  GetExternalApiDetailResponse,
  GetIndustryResponse,
  LeadInvalidLostReason,
  GetInvalidLostReasonResponse,
  GetExternalApiKeysResponse,
  GetCustomLeadFormFields,
} from 'services/types/settings'

import { SettingsState } from 'types/store'
import { setLoading, setSuccess, unSet } from 'api/remoteData'
import { CORE_LEAD_STATUS, FETCHING_STATUS } from 'utils/constants'
import { CustomFieldLeadDetail, CustomLeadFormField, LeadStatusDetail, OptionValue } from 'types/global'

import { LeadCustomFormField } from './types/lead'
import { ExternalApi } from './types/settings'
import { isEmpty } from 'lodash-es'

const initialState: SettingsState = {
  camsHosts: unSet(),
  externalApi: {
    list: unSet(),
    detail: unSet(),
    apiKeys: unSet(),
  },
  workflow: {
    contactInfoTypeList: unSet(),
    customLeadFormFields: unSet(),
    leadLostReasons: unSet(),
    leadInvalidReasons: unSet(),
    leadStatuses: unSet(),
    leadTypes: unSet(),
    lookups: undefined,
  },
  industries: unSet(),
  preLoginTranslation: {
    en: {
      lastModifiedTs: null,
      translation: null,
    },
    'zh-Hans': {
      lastModifiedTs: null,
      translation: null,
    },
    'zh-Hant': {
      lastModifiedTs: null,
      translation: null,
    },
    ja: {
      lastModifiedTs: null,
      translation: null,
    },
  },
}

const { reducer, actions } = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    clear: () => initialState,
    clearCustomLeadFormFields: (state) => ({
      ...state,
      workflow: { ...state.workflow, customLeadFormFields: unSet() },
    }),
    clearLeadStatuses: (state) => ({ ...state, workflow: { ...state.workflow, leadStatuses: unSet() } }),
    setPreLoinLstTranslation: (
      state: SettingsState,
      { payload }: { payload: { lang: string; lastModifiedTs: string; translation: { [x: string]: string } | null } },
    ) => {
      const { lang, lastModifiedTs, translation } = payload

      return {
        ...state,
        preLoginTranslation: {
          ...state.preLoginTranslation,
          [lang]: {
            lastModifiedTs,
            translation,
          },
        },
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getLeadInvalidLostReasons.fulfilled, (state, { payload }) => {
      if (payload.status === 'FAIL') {
        return {
          ...state,
          workflow: {
            ...state.workflow,
            leadInvalidReasons: payload,
            leadLostReasons: payload,
          },
        }
      }
      return {
        ...state,
        workflow: {
          ...state.workflow,
          leadInvalidReasons: setSuccess(mapLeadInvalidLostReasons(payload.data, CORE_LEAD_STATUS.Invalid)),
          leadLostReasons: setSuccess(mapLeadInvalidLostReasons(payload.data, CORE_LEAD_STATUS.Lost)),
        },
      }
    })

    builder.addCase(getContactInfoTypes.fulfilled, (state, { payload }) => {
      const data = (payload || []).map(({ id, name, is_active, is_locked }: GetContactInfoTypeResponse) => ({
        id,
        name,
        isLocked: is_locked,
        isActive: is_active,
      }))
      state.workflow.contactInfoTypeList = setSuccess([
        ...data.filter(({ name }) => name.toLowerCase() === 'email' || name.toLowerCase() === 'mobile'),
        ...data.filter(({ name }) => name.toLowerCase() !== 'email' && name.toLowerCase() !== 'mobile'),
      ])
    })

    /**
     * Custom Lead Form Fields
     */
    builder
      .addCase(getCustomLeadFormFields.pending, (state) => ({
        ...state,
        workflow: { ...state.workflow, customLeadFormFields: setLoading() },
      }))
      .addCase(getCustomLeadFormFields.fulfilled, (state, { payload }) => {
        if (payload.status === FETCHING_STATUS.fail) {
          return { ...state, workflow: { ...state.workflow, customLeadFormFields: payload } }
        }

        return {
          ...state,
          workflow: { ...state.workflow, customLeadFormFields: setSuccess(mapCustomLeadFormFields(payload.data)) },
        }
      })

    builder.addCase(getLeadStatuses.pending, (state, { meta }) => ({
      ...state,
      workflow: {
        ...state.workflow,
        leadStatuses: meta.arg?.shouldSoftUpdate ? state.workflow.leadStatuses : setLoading(meta.requestId),
      },
    }))

    builder.addCase(getLeadStatuses.fulfilled, (state, { payload }) => {
      if (payload.status === FETCHING_STATUS.fail) {
        return { ...state, leadStatuses: payload }
      }

      return {
        ...state,
        workflow: { ...state.workflow, leadStatuses: setSuccess(mapLeadStatusesDetail(payload.data)) },
      }
    })

    builder.addCase(getIndustries.fulfilled, (state, { payload }) => {
      state.industries = setSuccess(
        (payload || []).map(({ id, name, is_active }: GetIndustryResponse) => ({ id, name, isActive: is_active })),
      )
    })

    builder.addCase(getExternalApiList.pending, (state, { meta }) => ({
      ...state,
      externalApi: {
        ...current(state).externalApi,
        list: setLoading(meta.requestId),
      },
    }))

    builder.addCase(getExternalApiList.fulfilled, (state, { meta, payload }) => {
      if (payload.status === 'FAIL') {
        return {
          ...state,

          externalApi: { ...current(state).externalApi, list: payload },
        }
      }

      return {
        ...state,
        externalApi: {
          ...current(state).externalApi,
          list: setSuccess(payload.data, meta.requestId),
        },
      }
    })

    builder.addCase(getExternalApiDetail.pending, (state, { meta }) => ({
      ...state,
      externalApi: {
        ...current(state).externalApi,
        detail: setLoading(meta.requestId),
      },
    }))

    builder.addCase(getExternalApiDetail.fulfilled, (state, { meta, payload }) => {
      if (payload.status === 'FAIL') {
        return {
          ...state,
          externalApi: {
            ...current(state).externalApi,
            detail: payload,
          },
        }
      }

      return {
        ...state,
        externalApi: {
          ...current(state).externalApi,
          detail: setSuccess(mapExternalApiDetail(payload.data), meta.requestId),
        },
      }
    })

    builder.addCase(getExternalApiKeys.pending, (state, { meta }) => ({
      ...state,
      externalApi: {
        ...current(state).externalApi,
        apiKeys: setLoading(meta.requestId),
      },
    }))

    builder.addCase(getExternalApiKeys.fulfilled, (state, { meta, payload }) => {
      if (payload.status === 'FAIL') {
        return {
          ...state,
          externalApi: {
            ...current(state).externalApi,
            apiKeys: payload,
          },
        }
      }

      return {
        ...state,
        externalApi: {
          ...current(state).externalApi,
          apiKeys: setSuccess(mapExternalApiKeys(payload.data), meta.requestId),
        },
      }
    })

    /**
     * CAMS
     */
    builder
      .addCase(getCamsHosts.pending, (state) => ({ ...state, camsHosts: setLoading() }))
      .addCase(getCamsHosts.fulfilled, (state, { payload }) => {
        if (payload.status === FETCHING_STATUS.fail) {
          return { ...state, camsHosts: payload }
        }

        return {
          ...state,
          camsHosts: setSuccess(
            payload.data.map((i) => ({ camsHostId: i.cams_host_id, connection: i.connection, name: i.name })),
          ),
        }
      })
  },
})

const mapExternalApiDetail: (data: GetExternalApiDetailResponse) => ExternalApi.Detail = ({
  id,
  description,
  name,
  url,
  body,
  headers,
}) => ({
  id,
  name,
  url,
  description,
  headers: {
    accept: headers.Accept,
    appId: headers.App_ID,
    appVersion: headers.App_Version,
    branchId: headers.Branch_ID,
    contentLength: headers.Content_Length,
    contentType: headers.Content_Type,
    userAgent: headers.User_Agent,
  },
  body,
})

const mapLeadStatusesDetail: (data: GetLeadStatusesResponse) => LeadStatusDetail[] = (data) =>
  data.map((i) => ({
    leadStatusGroupId: i.lead_status_group_id,
    leadStatusGroupName: i.lead_status_group_name,
    leadStatusGroupDescription: i.lead_status_group_description,
    coreDescription: i.core_lead_status_description,
    coreLeadStatusId: i.core_lead_status_id,
    coreName: i.core_lead_status_name,
    createTs: i.create_ts,
    description: i.description,
    id: i.id,
    isEndPipeline: i.is_end_pipeline,
    isNegative: i.is_negative,
    isStartPipeline: i.is_start_pipeline,
    name: i.name,
  }))

const mapExternalApiKeys: (data: GetExternalApiKeysResponse) => ExternalApi.ApiKey[] = (data) =>
  data.data?.map((i) => ({
    apiKeyId: i.api_key_id,
    token: i.key,
    createTs: i.create_ts,
    expired: i.expired,
    status: i.status,
  }))

export default reducer
export { actions }

const mapLeadInvalidLostReasons = (reasons: GetInvalidLostReasonResponse, filterStatus: CORE_LEAD_STATUS) =>
  reasons
    .map(({ id, name, core_lead_status_id, lead_process_id, create_ts }: LeadInvalidLostReason) => ({
      id,
      name,
      coreLeadStatusId: core_lead_status_id,
      leadProcessId: lead_process_id,
      createTs: create_ts,
    }))
    .filter((item) => +item.coreLeadStatusId === filterStatus)

export const mapCustomFieldOptions = (options: OptionValue<string>[], translations?: string[]) => {
  if (Array.isArray(options) && !isEmpty(options)) {
    return options.map((opt) => {
      switch (opt.label) {
        case ' - None - ':
          return { ...opt, label: translations?.[0] || opt.label }
        case 'None':
          return { ...opt, label: translations?.[1] || opt.label }
        default:
          return opt
      }
    })
  } else {
    return options
  }
}

export const mapCustomLeadFormFields: (customField: GetCustomLeadFormFields) => CustomLeadFormField[] = (customField) =>
  customField.data.map((field) => ({
    ...field,
    customLeadFormFieldId: field.custom_form_field_id,
    custom_lead_form_field_type_accessor: field.type_accessor,
  }))

export const mapCustomFieldLeadDetail: (customField: CustomFieldLeadDetail[]) => LeadCustomFormField[] = (
  customField,
) =>
  customField.map((item) => ({
    customFormFieldId: item.custom_form_field_id,
    customFormFieldAccessor: item.custom_form_field_accessor,
    customFormFieldName: item.custom_form_field_name,
    customFormFieldType: item.custom_form_field_type,
    value: item.value,
  }))
